/**
 * Le module **Finance et réconciliation** — rapprochement, cantonnement, file
 * d'écarts, clôture comptable (`EX-REC-003`, `EX-REC-005`, `EX-LDG-002`,
 * `EX-BO-003`).
 *
 * Maquette : artboard `b_finance` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * `test/app/finance-maquette.test.tsx` **relit** cette maquette et la confronte
 * au livré, plutôt que d'en recopier les valeurs ici : un attendu dérivé du code
 * éprouvé n'est pas un attendu (`D-34`).
 *
 * ## Ce que ce chantier referme
 *
 * `ReconciliationApi` servait **quatre routes, éprouvées, et appelées par aucun
 * écran** — la neuvième occurrence de la famille que `D-25`, `D-30`, `D-35`,
 * `D-45` et `D-57` décrivent. Le contrôle des routes les nomme depuis que `D-57`
 * est soldée ; celles-ci étaient quatre des huit. Elles ont maintenant leur
 * écran :
 *
 *  - `GET /reconciliation/reports/{date}` — la journée : cantonnement,
 *    rapprochement, écarts du jour ;
 *  - `GET /reconciliation/discrepancies` — la file des écarts non résolus ;
 *  - `GET /reconciliation/discrepancies/{id}` — un écart et **tout** son
 *    historique, dans une seule réponse ;
 *  - `GET /reconciliation/periods` — les périodes comptables, et l'état de
 *    chacune.
 *
 * ## `controlled` et `reconciled` sont rendus AVANT tout le reste
 *
 * C'est le seul point de cet écran qu'il ne faut pas manquer. Un rapport vide
 * n'est pas « aucun écart », c'est « **aucun contrôle ce jour-là** » — et c'est
 * exactement la confusion qui a laissé `D-53` vivre depuis la story 1.4 : trois
 * contrôles financiers quotidiens écrits, verts, et que rien n'appelait, sur une
 * pile qui portait 34 écritures pour 0 photo. Un écran qui rendrait la journée
 * « saine » parce qu'aucune ligne d'écart n'est arrivée referait la même faute,
 * un cran plus haut — et cette fois devant un contrôleur qui signe.
 *
 * L'écran dit donc, en premier et en toutes lettres, si la journée a été
 * **contrôlée** et si elle a été **rapprochée**. Les chiffres viennent après, et
 * seulement s'il y en a.
 *
 * ## Les montants sont des entiers d'unités mineures, SANS devise
 *
 * Aucun des quatre contrats ne sert de code de devise. Mettre les montants en
 * forme monétaire demanderait d'en choisir une ici — sur l'écran où l'on décide
 * d'une écriture comptable, ce serait la pire des inventions. Ils sont rendus
 * comme des nombres groupés dans la langue de l'agent, et l'écran **dit** qu'ils
 * sont en unités mineures. C'est aussi ce qui rend un écart d'une unité visible,
 * ce que ce chantier cherche précisément à voir.
 *
 * **Le `gap` du cantonnement garde son signe**, et il le porte même à zéro :
 * positif, les détenteurs possèdent plus que ce que le cantonnement couvre —
 * c'est l'incident ; négatif, le cantonnement couvre davantage, ce qui est
 * l'état normal dès qu'un frais a été prélevé. Un écran qui en montrerait la
 * valeur absolue alarmerait à chaque frais.
 *
 * ## Les trois actions du dessin, et ce que chacune devient
 *
 * La maquette pose trois boutons. **Un seul est offert**, et les deux autres
 * sont nommés plutôt que tus — une divergence tue est une divergence que la
 * revue suivante refera.
 *
 *  1. **« Écriture de régularisation » — NON offerte, et c'est le point le plus
 *     important de cet écran.** L'action existe : `ACCOUNTING_ADJUSTMENT` est
 *     dans la matrice depuis la `V7`, en ✔✔ pour la finance, et
 *     `AccountingAdjustmentExecutor` lui donne son effet — une écriture
 *     **ajoutée** au grand livre, jamais une écriture modifiée (`EX-LDG-002`).
 *     Mais son exécuteur exige `debitAccountId` et `creditAccountId`, deux
 *     identifiants de comptes du grand livre, et **aucune route du back-office
 *     ne sert de plan de comptes** : les onze correspondances de
 *     `BackOfficeApi`, `KycReviewApi` et `ReconciliationApi` n'en portent
 *     aucune. Un formulaire qui demanderait à un agent de taper deux `UUID` de
 *     comptes qu'il n'a aucun moyen de connaître serait un bouton qu'on ne peut
 *     pas utiliser — la forme la plus coûteuse du bouton inerte, parce qu'elle a
 *     l'air d'une fonction. L'écart est déclaré à l'endroit exact où la revue
 *     suivante irait le combler : dans le panneau d'un écart ouvert.
 *  2. **« Exporter la balance » — aucune route.** Rien ne sert de balance, ni
 *     en lecture ni en export.
 *  3. **« Clôturer la journée » — offerte, sur la PÉRIODE et non sur la
 *     journée.** `PERIOD_CLOSE` est servie, elle est en ✔✔, et le seul champ
 *     qu'elle exige — `periodId` — est **servi par `GET /periods`**. L'écran la
 *     demande ; il ne la prononce pas. La seconde signature se donne dans la
 *     file de double validation déjà livrée, celle du tableau de bord : c'est
 *     `EX-BO-003`, et brancher un second mécanisme ici aurait été une seconde
 *     porte, avec son propre contrôle d'accès à tenir à jour. Le dessin écrit
 *     « la journée » ; le serveur clôt une **période comptable**, et aucune
 *     route ne dit à quelle période appartient une journée donnée. L'écran
 *     nomme donc la période par ses bornes servies, jamais par un jour.
 *
 * ## Ce que la maquette dessine encore, et que le produit ne reprend pas
 *
 * Chaque omission a la même cause : **aucune route ne la sert**.
 *
 *  1. **le taux « Rapproché 99,97 % »** — celui qu'il ne faut surtout pas
 *     dessiner. `RunView` sert `matchedCount` et `matchedAmount` ; il ne sert
 *     **aucun attendu**. Un pourcentage aurait donc un numérateur servi et un
 *     dénominateur inventé, et se lirait comme une assurance que personne n'a
 *     calculée. Sur un écran comptable, c'est pire que partout ailleurs ;
 *  2. **l'indicateur « Journée : non clôturée »** — aucune route ne dit si une
 *     journée donnée tombe dans une période close. Le déduire des bornes de
 *     période demanderait de trancher ici si une borne est incluse, ce que le
 *     contrat ne dit pas. La table des périodes montre lesquelles sont closes,
 *     et l'agent lit ;
 *  3. **la table « Source / Date / Attendu / Rapproché / Écart »** — la maquette
 *     la remplit de **cinq sources** rapprochées ; le contrat sert **un** relevé
 *     par journée (`statementId`) et aucun montant attendu. Ni la ventilation
 *     par source ni la colonne « Attendu » n'existent. La table de cet écran est
 *     donc la **file des écarts**, dont les cinq colonnes viennent toutes de
 *     `DiscrepancyView` — et elle garde les proportions du dessin, qui, elles,
 *     sont le contrat au pixel près ;
 *  4. **le compte « 5 sources rapprochées automatiquement à 06:00 »** — ni le
 *     nombre de sources ni l'heure de la passe ne sont servis comme tels ;
 *  5. **les quatre pastilles** — Toutes les sources · Rapprochées · Écarts ·
 *     Orphelines : ce serait un filtre, et aucune des quatre routes n'en connaît
 *     un ;
 *  6. **trois des quatre filtres** — Source, État, Écart. Le quatrième,
 *     « Journée », **est** un paramètre servi : c'est le chemin de
 *     `/reports/{date}`, et c'est le seul champ de saisie de la lecture ;
 *  7. **les noms d'agents** — « Affecté à : S. Camara ». Le serveur rend des
 *     `UUID` de `admin.bo_user` ; aucune route ne sert le nom d'affichage, et
 *     l'écrire l'inventerait. Même écart que sur le journal d'audit et la file
 *     de double validation, rendu tel quel plutôt que maquillé.
 *
 * ## Le vocabulaire emprunté à la coquille
 *
 * `QueueShell` porte le parcours au clavier, le panneau de détail et la région
 * vivante ; ses libellés parlent de « dossier », parce qu'elle a été écrite pour
 * la revue KYC. Ils sont conservés tels quels, pour la raison déjà écrite sur
 * `AuditLogScreen`, `DashboardScreen` et `CustomersScreen` : réécrire les
 * chaînes de la coquille pour un quatrième appelant en ferait des chaînes par
 * écran, et c'est la coquille qui garantit que les quatre files se traversent
 * pareil.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { QueueShell } from '../shell/QueueShell.js';
import type { QueueColumn, QueueEntry } from '../shell/QueueShell.js';
import { QueueDetailSection, QueueFact } from '../shell/QueueShell.js';
import { useDeclareExport } from './export-context.js';
import { remettreLeFichier, toCsv } from './export-csv.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  fetchAccountingPeriods,
  fetchDailyReport,
  fetchDiscrepancy,
  fetchDiscrepancyQueue,
  requestPeriodClosure,
} from '../http/reconciliation.js';
import type {
  AccountingPeriod,
  ActionAcknowledgement,
  DailyReport,
  Discrepancy,
  DiscrepancyDetail,
} from '../http/reconciliation.js';

/**
 * Les cinq colonnes de la file des écarts, dans l'ordre de lecture.
 *
 * Chacune est servie par `DiscrepancyView` : `kind`, `businessDate`, `amount`,
 * `assignedTo`, `status`. Ce ne sont **pas** les cinq colonnes du dessin — voir
 * l'en-tête : sa table ventile par source, ce que le contrat ne sert pas. Les
 * **proportions**, elles, restent celles de l'artboard.
 */
export const discrepancyColumns: readonly QueueColumn[] = [
  // L'emphase suit la NATURE de la donnée, comme le dessin le fait de la
  // sienne : la nature de l'écart nomme la ligne, la journée et le montant sont
  // des nombres qui doivent s'aligner pour se comparer, l'affectataire est un
  // `UUID` qu'on ne lit pas et l'état un code. Les pastilles colorées du dessin
  // ne sont PAS reprises : elles porteraient un jugement — vert, orange,
  // rouge — sur des états que le serveur rend sans le porter.
  { id: 'kind', labelKey: 'finance.columns.kind', emphasis: 'strong' },
  { id: 'businessDate', labelKey: 'finance.columns.businessDate', emphasis: 'numeric' },
  { id: 'amount', labelKey: 'finance.columns.amount', emphasis: 'numeric' },
  { id: 'assignedTo', labelKey: 'finance.columns.assignedTo', emphasis: 'muted' },
  { id: 'status', labelKey: 'finance.columns.status', emphasis: 'muted' },
];

/**
 * Le seul champ de la lecture d'une journée, et il est obligatoire.
 *
 * Aucune valeur par défaut, et surtout pas « aujourd'hui » : la journée serait
 * alors celle de l'horloge du poste, sur l'écran où l'on décide d'un arrêté de
 * comptes. L'agent désigne la journée ; le serveur rend celle qu'il a comprise,
 * et l'écran affiche **ce que le serveur a rendu**.
 *
 * **C'est une date, il se rend donc en champ de date.** Il était livré en zone
 * de texte nue, sous un libellé qui disait « au format AAAA-MM-JJ » : on
 * demandait à un contrôleur financier de composer un ISO 8601 à la main,
 * plusieurs fois par jour. La faute d'un chiffre y est silencieuse — le serveur
 * répond, la journée est simplement une autre. `FilterDateField` ouvre un
 * calendrier **dessiné avec les jetons du produit** — et non celui du
 * navigateur, qui peint ses propres couleurs à côté d'une pilule dessinée —,
 * affiche la journée dans la locale du poste, et laisse partir la valeur **en
 * ISO**, qui est ce que `GET /reconciliation/reports/{date}` prend. Ce qui
 * s'affiche ne repart jamais : « 05/09 » et « 09/05 » sont deux journées.
 */
const champsDeJournee: readonly FormFieldDescriptor[] = [
  {
    name: 'businessDate',
    labelKey: 'finance.fields.businessDate',
    required: true,
    control: 'date',
    placeholderKey: 'finance.fields.businessDatePlaceholder',
  },
];

/** Le seul champ d'une demande de clôture, et il est obligatoire côté serveur. */
const champsDeCloture: readonly FormFieldDescriptor[] = [
  { name: 'reason', labelKey: 'finance.fields.closureReason', required: true },
];

/** L'écran : la journée, les écarts ouverts, les périodes et leur clôture. */
export function FinanceScreen(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();

  const [rapport, setRapport] = useState<DailyReport | null>(null);
  const [erreurRapport, setErreurRapport] = useState<string | null>(null);
  const [ecarts, setEcarts] = useState<readonly Discrepancy[] | null>(null);
  const [erreurEcarts, setErreurEcarts] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<DiscrepancyDetail | null>(null);
  const [erreurOuvert, setErreurOuvert] = useState<string | null>(null);
  const [periodes, setPeriodes] = useState<readonly AccountingPeriod[] | null>(null);
  const [erreurPeriodes, setErreurPeriodes] = useState<string | null>(null);
  const [demande, setDemande] = useState<ActionAcknowledgement | null>(null);
  const [erreurDemande, setErreurDemande] = useState<string | null>(null);

  const chargerEcarts = useCallback(async () => {
    try {
      setEcarts(await fetchDiscrepancyQueue(client));
      setErreurEcarts(null);
    } catch (echec) {
      // La file lue est effacée avec l'échec. La garder ferait lire « aucun
      // écart ouvert » sur une liste que le serveur a cessé de confirmer — la
      // meilleure des nouvelles, et la plus fausse.
      setEcarts(null);
      setErreurEcarts(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  const chargerPeriodes = useCallback(async () => {
    try {
      setPeriodes(await fetchAccountingPeriods(client));
      setErreurPeriodes(null);
    } catch (echec) {
      setPeriodes(null);
      setErreurPeriodes(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client]);

  useEffect(() => {
    void chargerEcarts();
    void chargerPeriodes();
  }, [chargerEcarts, chargerPeriodes]);

  const lireLaJournee = useCallback(
    async (valeurs: Record<string, string>) => {
      try {
        setRapport(await fetchDailyReport(client, valeurs.businessDate.trim()));
        setErreurRapport(null);
      } catch (echec) {
        // Le rapport précédent est effacé : garder celui du 4 sous une demande
        // du 5 ferait signer un arrêté sur la mauvaise journée.
        setRapport(null);
        setErreurRapport(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client],
  );

  /**
   * Ouvre un écart — **et c'est un second appel, jamais la ligne dépilée**.
   *
   * La ligne de la file ne porte pas l'historique ; le serveur rend l'écart
   * *et* son histoire ensemble, et c'est cette réponse-là qui est affichée. Un
   * panneau bâti sur la ligne déjà reçue montrerait un écart sans son
   * investigation, c'est-à-dire l'écart privé de ce qui permet d'en décider.
   */
  const ouvrir = useCallback(
    (ligne: QueueEntry) => {
      setOuvert(null);
      setErreurOuvert(null);
      void (async () => {
        try {
          setOuvert(await fetchDiscrepancy(client, ligne.id));
        } catch (echec) {
          setErreurOuvert(echec instanceof ApiError ? echec.messageKey : 'errors.server');
        }
      })();
    },
    [client],
  );

  const demanderLaCloture = useCallback(
    async (periodId: string, motif: string) => {
      setDemande(null);
      try {
        setDemande(await requestPeriodClosure(client, periodId, motif));
        setErreurDemande(null);
        // Les périodes sont relues : la demande n'en clôt aucune, mais c'est
        // le serveur qui doit le dire, pas cet écran.
        await chargerPeriodes();
      } catch (echec) {
        setErreurDemande(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [chargerPeriodes, client],
  );

  /**
   * Un montant servi, groupé dans la langue de l'agent — **sans devise**.
   *
   * Aucun des quatre contrats n'en sert une. `style: 'currency'` exigerait d'en
   * choisir une ici ; le libellé de la colonne dit à la place qu'il s'agit
   * d'unités mineures.
   */
  const montant = useCallback(
    (mineures: number): string => new Intl.NumberFormat(i18n.language).format(mineures),
    [i18n.language],
  );

  /**
   * L'écart de cantonnement, **avec son signe**, y compris quand il est nul.
   *
   * `signDisplay: 'exceptZero'` rend « +1 » et « −1 » distincts d'un coup d'œil,
   * et laisse « 0 » sans signe. Prendre la valeur absolue ferait alarmer à
   * chaque frais prélevé ; ne pas marquer le positif ferait lire l'incident
   * comme l'état normal.
   */
  const ecartSigne = useCallback(
    (mineures: number): string =>
      new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero' }).format(mineures),
    [i18n.language],
  );

  const instant = useCallback(
    (valeur: string): string =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(valeur)),
    [i18n.language],
  );

  const lignes: readonly QueueEntry[] = useMemo(
    () =>
      (ecarts ?? []).map((ecart) => ({
        id: ecart.id,
        reference: ecart.id,
        values: [
          ecart.kind,
          ecart.businessDate,
          montant(ecart.amount),
          ecart.assignedTo ?? t('finance.unassigned'),
          ecart.status,
        ],
      })),
    [ecarts, montant, t],
  );

  /**
   * **Ce que la barre supérieure exporte ici.**
   *
   * Le bouton « Exporter » de la coquille était inerte sur les seize pages. Il
   * n'est plus rendu que là où un écran déclare ce qu'il tient. Cet écran tient
   * la file des écarts ouverts — la route la rend ENTIÈRE, sans pagination —,
   * et c'est donc la file entière qui s'écrit, telle que la table l'affiche.
   * Sans écart, aucune déclaration : la barre ne dessine pas de bouton plutôt
   * que d'en dessiner un qui écrirait un fichier vide.
   */
  const exportDeLaBarre = useMemo(() => {
    if (lignes.length === 0) return null;
    return () => {
      remettreLeFichier(
        'finance-ecarts-ouverts.csv',
        toCsv(
          discrepancyColumns.map((colonne) => t(colonne.labelKey)),
          lignes.map((ligne) => ligne.values),
        ),
      );
    };
  }, [lignes, t]);
  useDeclareExport(exportDeLaBarre);

  return (
    <section className="finance" aria-label={t('finance.landmark')}>
      <section className="finance__block" aria-label={t('finance.day.landmark')}>
        <h2 className="finance__heading">{t('finance.day.heading')}</h2>
        {/* La rangée de filtres du dessin : la journée et sa commande sur UNE
            ligne, en tête du bloc. Le dessin ne pose pas un bloc empilé sur
            toute la largeur au-dessus de sa table ; il pose une rangée. */}
        <FormShell
          layout="inline"
          fields={champsDeJournee}
          onSubmit={lireLaJournee}
          submitLabelKey="finance.day.submit"
        />

        {erreurRapport === null ? null : (
          <p className="finance__notice" role="alert">
            {t(erreurRapport)}
          </p>
        )}

        {rapport === null ? (
          <p className="finance__idle">{t('finance.day.beforeQuery')}</p>
        ) : (
          <DailyReportPanel
            report={rapport}
            formatAmount={montant}
            formatGap={ecartSigne}
            formatInstant={instant}
          />
        )}
      </section>

      <section className="finance__block" aria-label={t('finance.queue.landmark')}>
        <h2 className="finance__heading">{t('finance.queue.heading')}</h2>

        {erreurEcarts === null ? null : (
          <p className="finance__notice" role="alert">
            {t(erreurEcarts)}
          </p>
        )}

        {ecarts === null ? (
          <p className="finance__idle">{t('finance.queue.loading')}</p>
        ) : (
          <>
            {/* Ce n'est pas un total inventé : la route rend la file COMPLÈTE
                des écarts non résolus, sans borne ni curseur, et le serveur en
                journalise lui-même la taille. */}
            <p className="finance__result" data-testid="finance-open-discrepancies">
              {t('finance.queue.result', { count: ecarts.length })}
            </p>
            <QueueShell
              columns={discrepancyColumns}
              entries={lignes}
              onOpen={ouvrir}
              renderDetail={() => (
                <DiscrepancyPanel
                  detail={ouvert}
                  error={erreurOuvert}
                  formatAmount={montant}
                  formatInstant={instant}
                />
              )}
            />
          </>
        )}
      </section>

      <section className="finance__block" aria-label={t('finance.periods.landmark')}>
        <h2 className="finance__heading">{t('finance.periods.heading')}</h2>
        {/* La promesse du dessin, dans les mots de l'écran et sous la même
            exigence : la clôture arrête les soldes et verrouille la période. */}
        <p className="finance__lock">{t('finance.periods.lockNotice')}</p>

        {erreurPeriodes === null ? null : (
          <p className="finance__notice" role="alert">
            {t(erreurPeriodes)}
          </p>
        )}

        {erreurDemande === null ? null : (
          <p className="finance__notice" role="alert">
            {t(erreurDemande)}
          </p>
        )}

        {demande === null ? null : <ClosureAcknowledgement acknowledgement={demande} />}

        {periodes === null ? (
          <p className="finance__idle">{t('finance.periods.loading')}</p>
        ) : (
          <AccountingPeriods
            periods={periodes}
            formatInstant={instant}
            onRequestClosure={demanderLaCloture}
          />
        )}
      </section>

      {/* **Ce que le serveur ne sert pas, déclaré — mais pas en travers de
          l'écran.** Six lignes tenaient le haut de la page, et le dessin n'a
          aucun bloc en tête. La déclaration reste entière, au mot près ; elle
          change de forme et de place, exactement comme sur le journal d'audit,
          et pour la même raison. Ces deux écrans-là sont ceux que le porteur a
          mis côte à côte avec le dessin. */}
      <details className="finance__omissions">
        <summary className="finance__omissions-summary">{t('finance.scopeSummary')}</summary>
        <p className="finance__omissions-body">{t('finance.scopeNotice')}</p>
      </details>
    </section>
  );
}

/**
 * Le rapport d'une journée.
 *
 * **Les deux booléens d'abord.** Tout le reste est conditionné à eux : une
 * journée non contrôlée n'a pas « zéro écart », elle n'a pas été mesurée. C'est
 * la faute de `D-53` portée à l'écran, et le seul endroit où l'écran refuse
 * activement de conclure.
 */
function DailyReportPanel({
  report,
  formatAmount,
  formatGap,
  formatInstant,
}: {
  readonly report: DailyReport;
  readonly formatAmount: (minorUnits: number) => string;
  readonly formatGap: (minorUnits: number) => string;
  readonly formatInstant: (value: string) => string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="finance__report" data-testid="finance-report">
      <dl className="finance__facts">
        <dt>{t('finance.day.businessDate')}</dt>
        <dd data-testid="finance-report-date">{report.businessDate}</dd>
        <dt>{t('finance.day.controlled')}</dt>
        <dd data-testid="finance-report-controlled">
          {t(report.controlled ? 'finance.day.controlledYes' : 'finance.day.controlledNo')}
        </dd>
        <dt>{t('finance.day.reconciled')}</dt>
        <dd data-testid="finance-report-reconciled">
          {t(report.reconciled ? 'finance.day.reconciledYes' : 'finance.day.reconciledNo')}
        </dd>
      </dl>

      {report.custody === null ? (
        // « Pas de ligne » n'est pas « écart nul ». Le dire est tout l'objet de
        // ce chantier ; le taire rendrait la journée rassurante sans mesure.
        <p className="finance__gap" data-testid="finance-custody-absent">
          {t('finance.day.custodyAbsent')}
        </p>
      ) : (
        <dl className="finance__facts" data-testid="finance-custody">
          <dt>{t('finance.day.custodyGap')}</dt>
          <dd data-testid="finance-custody-gap">{formatGap(report.custody.gap)}</dd>
          <dt>{t('finance.day.holdersBalance')}</dt>
          <dd>{formatAmount(report.custody.holdersBalance)}</dd>
          <dt>{t('finance.day.trustBalance')}</dt>
          <dd>{formatAmount(report.custody.trustBalance)}</dd>
          <dt>{t('finance.day.accountsPhotographed')}</dt>
          <dd>{String(report.custody.accountsPhotographed)}</dd>
          <dt>{t('finance.day.severity')}</dt>
          <dd data-testid="finance-custody-severity">{report.custody.severity}</dd>
          <dt>{t('finance.day.blocked')}</dt>
          <dd data-testid="finance-custody-blocked">
            {t(report.custody.blocked ? 'finance.day.blockedYes' : 'finance.day.blockedNo')}
          </dd>
          <dt>{t('finance.day.computedAt')}</dt>
          <dd>{formatInstant(report.custody.computedAt)}</dd>
        </dl>
      )}

      {report.reconciliation === null ? (
        <p className="finance__gap" data-testid="finance-run-absent">
          {t('finance.day.runAbsent')}
        </p>
      ) : (
        <dl className="finance__facts" data-testid="finance-run">
          <dt>{t('finance.day.statement')}</dt>
          <dd>{report.reconciliation.statementId}</dd>
          <dt>{t('finance.day.matchedCount')}</dt>
          <dd>{String(report.reconciliation.matchedCount)}</dd>
          <dt>{t('finance.day.matchedAmount')}</dt>
          <dd>{formatAmount(report.reconciliation.matchedAmount)}</dd>
          <dt>{t('finance.day.internalOrphans')}</dt>
          <dd data-testid="finance-run-internal-orphans">
            {String(report.reconciliation.internalOrphanCount)}
          </dd>
          <dt>{t('finance.day.externalOrphans')}</dt>
          <dd data-testid="finance-run-external-orphans">
            {String(report.reconciliation.externalOrphanCount)}
          </dd>
          <dt>{t('finance.day.ranAt')}</dt>
          <dd>{formatInstant(report.reconciliation.ranAt)}</dd>
        </dl>
      )}

      <p className="finance__result" data-testid="finance-report-discrepancies">
        {t('finance.day.discrepancies', { count: report.discrepancies.length })}
      </p>
    </div>
  );
}

/**
 * Le panneau d'un écart ouvert : ce que le **second** appel a rendu, et son
 * histoire entière.
 *
 * Il ne reçoit pas la ligne de la file, et c'est délibéré : lui donner la ligne
 * l'aurait laissé afficher un écart pendant que son historique n'était pas
 * encore là, ou après l'échec de l'appel — un écart sans son investigation, sur
 * l'écran où l'on décide de le régulariser.
 */
function DiscrepancyPanel({
  detail,
  error,
  formatAmount,
  formatInstant,
}: {
  readonly detail: DiscrepancyDetail | null;
  readonly error: string | null;
  readonly formatAmount: (minorUnits: number) => string;
  readonly formatInstant: (value: string) => string;
}): ReactNode {
  const { t } = useTranslation();
  if (error !== null) {
    return (
      <p className="finance__notice" role="alert">
        {t(error)}
      </p>
    );
  }
  if (detail === null) return <p className="finance__idle">{t('finance.queue.opening')}</p>;
  const ecart = detail.discrepancy;
  return (
    <>
      {/* Le panneau de la finance vient de la MÊME famille de dessin que celui
          du journal d'audit : le porteur les a vus tous les deux dans Chrome, et
          c'est la même carte. Il prend donc les mêmes sections — un titre en
          capitales, des lignes dont le libellé est à gauche et la valeur à
          droite sur la ligne de base. Le Résumé, lui, n'est plus écrit ici non
          plus : la coquille le bâtit à partir des cinq colonnes de la file.

          Les quatre lignes qui suivent ne sont donc PAS des redites du résumé —
          elles portent ce que la file ne montre pas : la date comptable, la
          nature détaillée, l'affectation, et les deux instants. */}
      <QueueDetailSection title={t('finance.detail.sectionTitle')}>
        <QueueFact label={t('finance.detail.kind')} testId="discrepancy-kind">
          {ecart.kind}
        </QueueFact>
        <QueueFact label={t('finance.detail.businessDate')} testId="discrepancy-business-date">
          {ecart.businessDate}
        </QueueFact>
        <QueueFact label={t('finance.detail.amount')} testId="discrepancy-amount">
          {formatAmount(ecart.amount)}
        </QueueFact>
        <QueueFact label={t('finance.detail.detail')} testId="discrepancy-detail">
          {ecart.detail}
        </QueueFact>
        <QueueFact label={t('finance.detail.status')} testId="discrepancy-status">
          {ecart.status}
        </QueueFact>
        <QueueFact label={t('finance.detail.assignedTo')} testId="discrepancy-assigned-to">
          {ecart.assignedTo ?? t('finance.unassigned')}
        </QueueFact>
        <QueueFact label={t('finance.detail.openedAt')}>{formatInstant(ecart.openedAt)}</QueueFact>
        <QueueFact label={t('finance.detail.resolvedAt')} testId="discrepancy-resolved-at">
          {/* Un écart non résolu n'a pas d'heure de résolution, et « absent »
              n'est pas une case vide : une case vide se lit comme une donnée
              manquante, pas comme un écart encore ouvert. */}
          {ecart.resolvedAt === null ? t('finance.unresolved') : formatInstant(ecart.resolvedAt)}
        </QueueFact>
        <QueueFact label={t('finance.detail.resolution')} testId="discrepancy-resolution">
          {ecart.resolution ?? t('finance.unresolved')}
        </QueueFact>
      </QueueDetailSection>

      <section className="queue__detail-section">
        <h3 className="queue__detail-section-title">{t('finance.detail.history')}</h3>
        {detail.history.length === 0 ? (
          <p className="finance__idle">{t('finance.detail.historyEmpty')}</p>
        ) : (
          <ol className="finance__history" data-testid="discrepancy-history">
            {detail.history.map((moment, rang) => (
              <li className="finance__moment" key={`${moment.occurredAt}-${String(rang)}`}>
                <span className="finance__moment-type">{moment.eventType}</span>
                <span className="finance__moment-actor">
                  {moment.actorId ?? t('finance.detail.noActor')}
                </span>
                <span className="finance__moment-note">{moment.note}</span>
                <span className="finance__moment-time">{formatInstant(moment.occurredAt)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* L'écart le plus coûteux de cet écran, nommé à l'endroit exact où la
          revue suivante irait le combler. `ACCOUNTING_ADJUSTMENT` existe et
          fonctionne ; ce sont ses deux comptes du grand livre qu'aucune route
          du back-office ne sert. */}
      <section className="queue__detail-section">
        <p className="finance__gap">{t('finance.adjustmentNotice')}</p>
      </section>
    </>
  );
}

/**
 * Les périodes comptables, et la demande de clôture de celles qui ne sont pas
 * closes.
 *
 * **C'est `closedAt` qui décide, pas `status`.** Le statut est un code du
 * serveur — `OUVERTE`, `CLOTUREE` — et le comparer ici écrirait dans le
 * navigateur une liste blanche que la base peut allonger sans prévenir : c'est
 * la faute de `G3`, et le premier statut ajouté la rendrait fausse en silence.
 * Une période sans **instant de clôture servi** n'a pas été close ; c'est un
 * fait, pas un code.
 */
function AccountingPeriods({
  periods,
  formatInstant,
  onRequestClosure,
}: {
  readonly periods: readonly AccountingPeriod[];
  readonly formatInstant: (value: string) => string;
  readonly onRequestClosure: (periodId: string, reason: string) => void | Promise<void>;
}): ReactNode {
  const { t } = useTranslation();
  if (periods.length === 0) {
    return <p className="finance__idle">{t('finance.periods.empty')}</p>;
  }
  return (
    <ul className="finance__periods" data-testid="finance-periods">
      {periods.map((periode) => (
        <li className="finance__period" key={periode.id}>
          <dl className="finance__facts">
            <dt>{t('finance.periods.span')}</dt>
            <dd data-testid={`period-span-${periode.id}`}>
              {t('finance.periods.spanValue', {
                start: periode.periodStart,
                end: periode.periodEnd,
              })}
            </dd>
            <dt>{t('finance.periods.status')}</dt>
            <dd data-testid={`period-status-${periode.id}`}>{periode.status}</dd>
            <dt>{t('finance.periods.closedAt')}</dt>
            <dd data-testid={`period-closed-at-${periode.id}`}>
              {periode.closedAt === null
                ? t('finance.periods.notClosed')
                : formatInstant(periode.closedAt)}
            </dd>
            <dt>{t('finance.periods.closedBy')}</dt>
            <dd>{periode.closedBy ?? t('finance.periods.notClosed')}</dd>
            <dt>{t('finance.periods.approvedBy')}</dt>
            <dd>{periode.approvedBy ?? t('finance.periods.notClosed')}</dd>
          </dl>

          {periode.closedAt !== null ? (
            // Une période close ne se reclôt pas, et le bouton n'est pas grisé :
            // il n'est pas là. Un bouton qui refuse toujours apprend à ne plus
            // cliquer, ce que ce dépôt refuse depuis six écrans.
            <p className="finance__idle" data-testid={`period-locked-${periode.id}`}>
              {t('finance.periods.alreadyClosed')}
            </p>
          ) : (
            <FormShell
              fields={champsDeCloture}
              onSubmit={(valeurs) => onRequestClosure(periode.id, valeurs.reason.trim())}
              submitLabelKey="finance.periods.submit"
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Ce que le socle a répondu à la demande de clôture.
 *
 * **L'écran ne dit jamais « la période est close ».** Tant que le serveur rend
 * un identifiant de demande, la clôture attend une seconde signature, et
 * l'annoncer close serait annoncer un arrêté de comptes qui n'a pas eu lieu.
 * La distinction est portée par la présence de `approvalRequestId`, servie, et
 * non par une comparaison à un code écrit ici.
 */
function ClosureAcknowledgement({
  acknowledgement,
}: {
  readonly acknowledgement: ActionAcknowledgement;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="finance__acknowledgement" data-testid="finance-closure-acknowledgement">
      {acknowledgement.approvalRequestId === null ? (
        <p className="finance__result">
          {t('finance.periods.outcome', { outcome: acknowledgement.outcome })}
        </p>
      ) : (
        <p className="finance__lock" data-testid="finance-closure-pending">
          {t('finance.periods.awaitingSecondSignature', {
            reference: acknowledgement.approvalRequestId,
          })}
        </p>
      )}
      {acknowledgement.detail === null ? null : (
        <p className="finance__result">{acknowledgement.detail}</p>
      )}
    </div>
  );
}
